<template>
  <div class="news">
    <button class="news-button" type="button" @click="openModal">
      NOTES
    </button>
    <div
      v-if="isNewsOpen"
      class="news-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="news-title"
      @click.self="closeNews"
    >
      <div ref="newsPanel" class="news-modal__panel">
        <div class="news-modal__header">
          <div>
            <p class="news-modal__eyebrow">Latest notes</p>
            <h2 id="news-title">Notes</h2>
          </div>
          <button
            class="news-modal__close"
            type="button"
            aria-label="Close notes"
            @click="closeNews"
          >
            ×
          </button>
        </div>
        <div v-if="isLoading" class="news-modal__loading">
          <span class="news-modal__spinner" aria-hidden="true"></span>
          <p>Loading notes</p>
        </div>
        <div v-else-if="!newsItems.length" class="news-modal__empty">
          <p>No notes published yet.</p>
        </div>
        <article v-else-if="selectedNews" class="news-modal__detail">
          <button
            class="news-modal__back"
            type="button"
            @click="selectedNews = null"
          >
            Back to notes
          </button>
          <img
            v-if="selectedNews.imageUrl"
            class="news-modal__image"
            :src="selectedNews.imageUrl"
            :alt="selectedNews.title"
          />
          <p class="news-modal__date">
            {{ selectedNews.displayDate || selectedNews.date }}
          </p>
          <p class="news-modal__author">
            By {{ selectedNews.author || "Rasha Corti" }}
          </p>
          <h3>{{ selectedNews.title }}</h3>
          <p class="news-modal__excerpt">
            {{ selectedNews.excerpt || selectedNews.text }}
          </p>
          <div
            v-if="selectedNews.body"
            class="news-modal__body"
            v-html="selectedNews.body"
          ></div>
          <button
            class="news-modal__back news-modal__back--bottom"
            type="button"
            @click="backToList"
          >
            Back to notes
          </button>
        </article>
        <div v-else class="news-modal__list">
          <article
            v-for="item in newsItems"
            :key="item.id || item.title"
            :class="{ 'news-modal__item--clickable': hasFullText(item) }"
            @click="openNews(item)"
          >
            <img
              v-if="item.imageUrl"
              class="news-modal__image"
              :src="item.imageUrl"
              :alt="item.title"
            />
            <p class="news-modal__date">{{ item.displayDate || item.date }}</p>
            <p class="news-modal__author">By {{ item.author || "Rasha Corti" }}</p>
            <h3>{{ item.title }}</h3>
            <p>{{ item.excerpt || item.text }}</p>
            <button
              v-if="hasFullText(item)"
              class="news-modal__read-more"
              type="button"
              @click.stop="openNews(item)"
            >
              Read full note
            </button>
          </article>
        </div>
        <button class="news-modal__bottom-close" type="button" @click="closeNews">
          Close
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { collection, getDocs, query, where } from "firebase/firestore";
import { db, isFirebaseConfigured } from "../firebase";
import { formatNewsDate } from "../news";

export default {
  name: "NewsButton",
  data() {
    return {
      isLoading: true,
      isNewsOpen: false,
      newsItems: [],
      scrollLockY: 0,
      selectedNews: null,
    };
  },
  mounted() {
    this.loadNews();
  },
  beforeUnmount() {
    this.unlockPageScroll();
  },
  methods: {
    openModal() {
      this.isNewsOpen = true;
      this.lockPageScroll();
      this.$nextTick(this.scrollPanelToTop);
    },
    closeNews() {
      this.isNewsOpen = false;
      this.selectedNews = null;
      this.unlockPageScroll();
    },
    backToList() {
      this.selectedNews = null;
      this.$nextTick(this.scrollPanelToTop);
    },
    lockPageScroll() {
      this.scrollLockY = window.scrollY || window.pageYOffset || 0;
      document.body.style.position = "fixed";
      document.body.style.top = `-${this.scrollLockY}px`;
      document.body.style.left = "0";
      document.body.style.right = "0";
      document.body.style.overflow = "hidden";
      document.body.style.width = "100%";
    },
    unlockPageScroll() {
      if (document.body.style.position !== "fixed") {
        return;
      }

      document.body.style.position = "";
      document.body.style.top = "";
      document.body.style.left = "";
      document.body.style.right = "";
      document.body.style.overflow = "";
      document.body.style.width = "";
      window.scrollTo(0, this.scrollLockY);
    },
    scrollPanelToTop() {
      if (this.$refs.newsPanel) {
        this.$refs.newsPanel.scrollTop = 0;
      }
    },
    async loadNews() {
      if (!isFirebaseConfigured) {
        this.isLoading = false;
        return;
      }

      try {
        const newsQuery = query(
          collection(db, "news"),
          where("published", "==", true)
        );
        const snapshot = await getDocs(newsQuery);
        const items = snapshot.docs.map((doc) => {
          const data = doc.data();
          return {
            id: doc.id,
            ...data,
            displayDate: formatNewsDate(data.date),
          };
        })
          .sort((a, b) => this.getSortTime(b.date) - this.getSortTime(a.date))
          .slice(0, 6);

        this.newsItems = items;
      } catch (error) {
        console.error("Failed to load notes", error);
        this.newsItems = [];
      } finally {
        this.isLoading = false;
      }
    },
    hasFullText(item) {
      return Boolean(item.body && item.body.replace(/<[^>]*>/g, "").trim());
    },
    openNews(item) {
      if (this.hasFullText(item)) {
        this.selectedNews = item;
        this.$nextTick(this.scrollPanelToTop);
      }
    },
    getSortTime(dateValue) {
      if (!dateValue) {
        return 0;
      }
      const date =
        typeof dateValue.toDate === "function" ? dateValue.toDate() : dateValue;
      return date.getTime ? date.getTime() : 0;
    },
  },
};
</script>

<style scoped>
.news {
  margin-top: 28px;
}

.news-button {
  animation: newsPulse 1.8s ease-in-out infinite;
  background: #13a1c7;
  color: #ffffff;
  font-family: "Poppins", sans-serif;
  font-size: 1rem;
  font-weight: 800;
  letter-spacing: 0.12em;
  padding: 14px 28px;
  text-transform: uppercase;
}

.news-button:hover {
  background: #20324f;
}

.news-modal {
  align-items: center;
  background: rgba(32, 50, 79, 0.62);
  display: flex;
  inset: 0;
  justify-content: center;
  overscroll-behavior: contain;
  padding: 32px;
  position: fixed;
  z-index: 50;
}

.news-modal__panel {
  background: #ffffff;
  color: #20324f;
  max-height: calc(100vh - 64px);
  max-width: 920px;
  overflow: auto;
  overscroll-behavior: contain;
  padding: 36px;
  width: min(100%, 920px);
}

.news-modal__bottom-close {
  background: #20324f;
  color: #ffffff;
  display: none;
  font-family: "Poppins", sans-serif;
  font-size: 0.84rem;
  font-weight: 800;
  letter-spacing: 0.1em;
  margin-top: 28px;
  padding: 13px 18px;
  text-transform: uppercase;
  width: 100%;
}

.news-modal__header {
  align-items: flex-start;
  display: flex;
  gap: 24px;
  justify-content: space-between;
  margin-bottom: 24px;
}

.news-modal__eyebrow {
  color: #13a1c7;
  font-family: "Poppins", sans-serif;
  font-size: 0.72rem;
  font-weight: 800;
  letter-spacing: 0.14em;
  margin: 0 0 8px;
  text-transform: uppercase;
}

.news-modal h2 {
  color: #20324f;
  font-size: 2rem;
  font-weight: 800;
  line-height: 1.1;
  margin: 0;
}

.news-modal__close {
  color: #20324f;
  font-size: 2rem;
  line-height: 1;
}

.news-modal__list {
  display: grid;
  gap: 22px;
}

.news-modal__loading,
.news-modal__empty {
  align-items: center;
  border-top: 1px solid rgba(19, 161, 199, 0.35);
  display: flex;
  gap: 14px;
  min-height: 180px;
  padding-top: 24px;
}

.news-modal__loading p,
.news-modal__empty p {
  color: #20324f;
  font-family: "Poppins", sans-serif;
  font-size: 0.95rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  margin: 0;
  text-transform: uppercase;
}

.news-modal__spinner {
  animation: newsSpin 0.8s linear infinite;
  border: 3px solid rgba(19, 161, 199, 0.2);
  border-top-color: #13a1c7;
  display: inline-block;
  height: 34px;
  width: 34px;
}

.news-modal__list article {
  border-top: 1px solid rgba(19, 161, 199, 0.35);
  padding-top: 18px;
}

.news-modal__item--clickable {
  cursor: pointer;
}

.news-modal__item--clickable:hover h3 {
  color: #13a1c7;
}

.news-modal__read-more,
.news-modal__back {
  background: transparent;
  color: #13a1c7;
  font-family: "Poppins", sans-serif;
  font-size: 0.78rem;
  font-weight: 800;
  letter-spacing: 0.1em;
  margin-top: 14px;
  padding: 0;
  text-transform: uppercase;
}

.news-modal__read-more:hover,
.news-modal__back:hover {
  color: #20324f;
}

.news-modal__back--bottom {
  border: 1px solid rgba(19, 161, 199, 0.45);
  display: inline-flex;
  justify-content: center;
  margin-top: 24px;
  padding: 12px 16px;
  width: 100%;
}

.news-modal__image {
  aspect-ratio: 16 / 9;
  margin-bottom: 14px;
  object-fit: cover;
  width: 100%;
}

.news-modal__date {
  color: #13a1c7;
  font-family: "Poppins", sans-serif;
  font-size: 0.72rem;
  font-weight: 800;
  letter-spacing: 0.1em;
  margin: 0 0 8px;
  text-transform: uppercase;
}

.news-modal__author {
  color: #20324f;
  font-family: "Poppins", sans-serif;
  font-size: 0.78rem;
  font-weight: 800;
  letter-spacing: 0.06em;
  margin: 0 0 10px !important;
  text-transform: uppercase;
}

.news-modal__list h3,
.news-modal__detail h3 {
  color: #20324f;
  font-size: 1.25rem;
  font-weight: 800;
  line-height: 1.25;
  margin: 0 0 8px;
}

.news-modal__list p,
.news-modal__detail p {
  line-height: 1.6;
  margin: 0;
}

.news-modal__detail {
  border-top: 1px solid rgba(19, 161, 199, 0.35);
  padding-top: 18px;
}

.news-modal__detail h3 {
  font-size: 1.75rem;
  margin-bottom: 12px;
}

.news-modal__excerpt {
  font-weight: 800;
  margin-bottom: 18px !important;
}

.news-modal__body {
  display: grid;
  gap: 8px;
  line-height: 1.55;
  margin-top: 18px;
}

.news-modal__body :deep(h1) {
  color: #13a1c7;
  font-size: 2rem;
  font-weight: 800;
  line-height: 1.12;
  margin: 0;
}

.news-modal__body :deep(h2) {
  color: #20324f;
  font-size: 1.35rem;
  font-weight: 800;
  line-height: 1.25;
  margin: 0;
}

.news-modal__body :deep(p) {
  margin: 0;
}

.news-modal__body :deep(ul) {
  list-style: disc;
  margin: 0 0 0 22px;
}

.news-modal__body :deep(a) {
  color: #13a1c7;
  font-weight: 800;
  text-decoration: underline;
}

.news-modal__body :deep(strong),
.news-modal__body :deep(b) {
  color: #20324f;
  font-weight: 800;
}

.news-modal__body :deep(em),
.news-modal__body :deep(i) {
  font-style: italic;
}

.news-modal__body :deep(u) {
  text-decoration: underline;
  text-decoration-color: #13a1c7;
  text-decoration-thickness: 2px;
  text-underline-offset: 3px;
}

@keyframes newsPulse {
  0%,
  100% {
    box-shadow: 0 0 0 0 rgba(19, 161, 199, 0.42);
  }

  50% {
    box-shadow: 0 0 0 10px rgba(19, 161, 199, 0);
  }
}

@keyframes newsSpin {
  to {
    transform: rotate(360deg);
  }
}

@media (max-width: 767px) {
  .news {
    text-align: center;
  }

  .news-modal {
    align-items: stretch;
    padding: 10px;
  }

  .news-modal__panel {
    max-height: calc(100dvh - 20px);
    padding: 24px 18px 18px;
    text-align: left;
  }

  .news-modal__header {
    gap: 14px;
    margin-bottom: 20px;
  }

  .news-modal h2 {
    font-size: 2.35rem;
  }

  .news-modal__close {
    font-size: 2.4rem;
    padding: 0 0 8px 8px;
  }

  .news-modal__list {
    gap: 18px;
  }

  .news-modal__date,
  .news-modal__author {
    text-align: left;
  }

  .news-modal__list h3,
  .news-modal__detail h3 {
    font-size: 1.55rem;
    line-height: 1.16;
  }

  .news-modal__list p,
  .news-modal__detail p,
  .news-modal__body {
    font-size: 1.02rem;
    line-height: 1.48;
    text-align: left;
  }

  .news-modal__excerpt {
    font-weight: 700;
  }

  .news-modal__body :deep(h1) {
    font-size: 1.7rem;
  }

  .news-modal__body :deep(h2) {
    font-size: 1.3rem;
  }

  .news-modal__bottom-close {
    display: block;
  }

  .news-modal__back--bottom {
    background: #20324f;
    border: 0;
    color: #ffffff;
    margin-top: 28px;
    padding: 14px 18px;
  }

  .news-modal__back--bottom:hover {
    color: #ffffff;
  }
}
</style>
